import { memo, useEffect, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import instance from 'services/api';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, MenuItem } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import Preloader from 'components/Preloader';
import CustomTextInput from 'components/Forms/CustomTextInput';
import { SuperAdminLoadingButton } from 'redesign/Shared/styles';
import { updateCurrentBusiness } from 'redux/actions/business';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import {
  PROFIT_IQ_STATES,
  profitIqSettingsSchema,
} from 'pages/Settings/ProfitIqSettings/profitIqSettingsSchema';
import {
  BusinessSettingsSubTitle,
  BusinessSettingsTitle,
} from 'pages/Settings/BusinessSettings/styles';

const DATE_INPUT = 'YYYY-MM-DD';

const today = () => moment().format(DATE_INPUT);

// A stored config arrives with ISO timestamps, which the date input cannot
// take, and with a `trialEnd` the form never edits. A business that has never
// been on a trial starts the date on today, so granting one only takes picking
// the state and the length.
const toFormValues = (config) => {
  if (!config?.state) {
    return {
      state: PROFIT_IQ_STATES.disabled,
      trialStart: today(),
      trialPeriodInDay: null,
    };
  }
  return {
    state: config.state,
    trialStart: config.trialStart
      ? moment(config.trialStart).format(DATE_INPUT)
      : today(),
    trialPeriodInDay: config.trialPeriodInDay ?? null,
  };
};

const ProfitIqSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const { currentBusiness, isBrandLoading } = useSelector(
    ({ business }) => business
  );

  const profitIqSettings = useForm({
    defaultValues: toFormValues(currentBusiness?.settings?.profitIqConfig),
    mode: 'all',
    resolver: yupResolver(profitIqSettingsSchema),
  });

  const state = profitIqSettings.watch('state');
  const trialStart = profitIqSettings.watch('trialStart');
  const trialPeriodInDay = profitIqSettings.watch('trialPeriodInDay');
  const isTrial = state === PROFIT_IQ_STATES.trial;

  // The expiry the backend will store, shown before saving so the dates can be
  // checked against each other.
  const resolvedTrialEnd =
    isTrial && trialStart && trialPeriodInDay >= 1
      ? moment(trialStart).add(trialPeriodInDay, 'days').format('MMM D, YYYY')
      : '—';

  useEffect(() => {
    profitIqSettings.reset(
      toFormValues(currentBusiness?.settings?.profitIqConfig),
      { keepDirty: false }
    );
  }, [currentBusiness?.settings?.profitIqConfig]);

  const handleProfitIqSettingsSubmit = (values) => {
    setLoading(true);
    const payload = {
      state: values.state,
      ...(values.state === PROFIT_IQ_STATES.trial
        ? {
            trialStart: moment(values.trialStart).format(DATE_INPUT),
            trialPeriodInDay: Number(values.trialPeriodInDay),
          }
        : {}),
    };

    return instance
      .patch('/business/profit-iq', payload)
      .then((res) => {
        const saved = res?.data?.data?.settings?.profitIqConfig;
        profitIqSettings.reset(toFormValues(saved), { keepDirty: false });
        dispatch(
          updateCurrentBusiness({
            ...currentBusiness,
            settings: {
              ...currentBusiness.settings,
              profitIqConfig: saved,
            },
          })
        );
        setLoading(false);
        toast.success('Setting is successfully saved');
      })
      .catch(() => setLoading(false));
  };

  return (
    <>
      {isBrandLoading && <Preloader />}
      {!isBrandLoading && (
        <FormProvider {...profitIqSettings}>
          <ConfirmPromptModal
            handleConfirm={profitIqSettings.handleSubmit(
              handleProfitIqSettingsSubmit
            )}
            submitData={profitIqSettings.getValues()}
            hasUnsavedChanges={profitIqSettings.formState.isDirty}
            formState={profitIqSettings}
          />
          <form
            onSubmit={profitIqSettings.handleSubmit(
              handleProfitIqSettingsSubmit
            )}
          >
            <Grid container>
              <Grid item xs={12}>
                <BusinessSettingsTitle>
                  {t('settings.profitIq_settings')}
                </BusinessSettingsTitle>
                <BusinessSettingsSubTitle>
                  {t('settings.profitIq_settings_description')}
                </BusinessSettingsSubTitle>
              </Grid>
              <Grid item xs={12}>
                <CustomTextInput
                  sx={{ width: '100%', mt: 2, maxWidth: '50%' }}
                  select={true}
                  name="state"
                  label={t('settings.profitIq_state')}
                >
                  <MenuItem value={PROFIT_IQ_STATES.allowed}>
                    {t('settings.profitIq_state_allowed')}
                  </MenuItem>
                  <MenuItem value={PROFIT_IQ_STATES.disabled}>
                    {t('settings.profitIq_state_disabled')}
                  </MenuItem>
                  <MenuItem value={PROFIT_IQ_STATES.trial}>
                    {t('settings.profitIq_state_trial')}
                  </MenuItem>
                </CustomTextInput>
              </Grid>
              {isTrial && (
                <>
                  <Grid item xs={12}>
                    <CustomTextInput
                      sx={{ width: '100%', mt: 2, maxWidth: '50%' }}
                      size="small"
                      name="trialStart"
                      label={t('settings.profitIq_trial_start')}
                      inputProps={{ type: 'date' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextInput
                      sx={{ width: '100%', mt: 2, maxWidth: '50%' }}
                      size="small"
                      name="trialPeriodInDay"
                      label={t('settings.profitIq_trial_period')}
                      inputProps={{ type: 'number', min: 1, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <BusinessSettingsSubTitle sx={{ mt: 2 }}>
                      {`${t('settings.profitIq_trial_end')}: ${resolvedTrialEnd}`}
                    </BusinessSettingsSubTitle>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <SuperAdminLoadingButton
                  id="profitIqSettingsLoading"
                  disabled={!profitIqSettings.formState.isDirty || loading}
                  variant="contained"
                  type="submit"
                  sx={{ width: '200px', mt: 2, alignItems: 'right', p: 1 }}
                  loading={loading}
                >
                  {t('settings.save')}
                </SuperAdminLoadingButton>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default memo(ProfitIqSettings);
